import { z } from "zod";
import { Version } from "./concurrent.js";

/**
 * 端数処理（#108、docs/41、DB 20261001_1200）
 *   会社の設定は「向き」（切り捨て／四捨五入／切り上げ）を 明細と率の行・消費税 に1つずつと、マイナスの金額の基準を1つ。
 *   見積を作るときに、向き×基準を1つの「コード」にして回（document_revisions）に写し、出した時点で固定する。
 *   請求書・領収書・分割の回は見積の回のコードを引き継ぐ。設定を変えても作りかけの見積は自動では変えない（★3。見積の画面の「既定値で再計算」）
 */

/** 丸めの向き（tenants.line_amount_rounding／tax_rounding_mode、partners.tax_rounding_mode） */
export const RoundingDirection = z.enum(["floor", "round", "ceil"]);
export type RoundingDirection = z.infer<typeof RoundingDirection>;

/** マイナスの金額の基準（tenants.negative_rounding）。abs＝絶対値（Excel と同じ。既定）／value＝数値 */
export const NegativeRounding = z.enum(["abs", "value"]);
export type NegativeRounding = z.infer<typeof NegativeRounding>;

/**
 * 回に写すコード（document_revisions.line_rounding_mode／rounding_mode）。
 *   floor＝切り捨て・数値／trunc＝切り捨て・絶対値／ceil＝切り上げ・数値／away＝切り上げ・絶対値／round＝四捨五入・絶対値／half_up＝四捨五入・数値。
 *   floor・ceil・round は 20261001_1200 より前の値そのまま（意味も同じ）
 */
export const RoundingCode = z.enum(["floor", "trunc", "ceil", "away", "round", "half_up"]);
export type RoundingCode = z.infer<typeof RoundingCode>;

export const ROUNDING_LABEL: Record<RoundingDirection, string> = { floor: "切り捨て", round: "四捨五入", ceil: "切り上げ" };
export const NEGATIVE_ROUNDING_LABEL: Record<NegativeRounding, string> = { abs: "絶対値基準", value: "数値基準" };
/** マイナスの例（−29.9 の切り捨て）。設定の画面の選択肢の下に出す */
export const NEGATIVE_ROUNDING_EXAMPLE: Record<NegativeRounding, string> = { abs: "−29.9 を切り捨て → −29（Excel と同じ）", value: "−29.9 を切り捨て → −30" };

export const roundingDirectionOf = (c: RoundingCode): RoundingDirection => (c === "floor" || c === "trunc" ? "floor" : c === "ceil" || c === "away" ? "ceil" : "round");
export const negativeRoundingOf = (c: RoundingCode): NegativeRounding => (c === "floor" || c === "ceil" || c === "half_up" ? "value" : "abs");
export function roundingCode(d: RoundingDirection, b: NegativeRounding): RoundingCode {
  if (d === "floor") return b === "value" ? "floor" : "trunc";
  if (d === "ceil") return b === "value" ? "ceil" : "away";
  return b === "value" ? "half_up" : "round";
}
/** 画面の言葉（切り捨て／四捨五入／切り上げ） */
export const roundingCodeLabel = (c: RoundingCode): string => ROUNDING_LABEL[roundingDirectionOf(c)];

/**
 * 金額の丸め（DB の fn_round_amount と同じ決まり。6つのコード、知らない値は四捨五入）。
 * 画面で「数量×単価」を見せるとき（受け取った書類の確認の引き出しなど）だけ使う。正は DB が計算した値
 */
export function roundAmount(value: number, code: RoundingCode | string): number {
  const v = Number.isFinite(value) ? value : 0;
  const r = (() => {
    switch (code) {
      case "ceil":
        return Math.ceil(v);
      case "floor":
        return Math.floor(v);
      case "trunc":
        return Math.trunc(v);
      case "away":
        return Math.sign(v) * Math.ceil(Math.abs(v));
      case "half_up":
        return Math.floor(v + 0.5);
      default:
        return Math.sign(v) * Math.round(Math.abs(v));
    }
  })();
  return r === 0 ? 0 : r; // -0 を 0 に
}

/** GET/PUT /api/v1/company/rounding。PUT は管理者だけ */
export const RoundingSettings = z.object({
  /** 明細（数量×単価）と率の行（基礎×率）の1円未満。既定 round */
  lineRounding: RoundingDirection,
  /** 消費税（税率ごとの合計に1回）。既定 floor。顧客ごとに上書きできる */
  taxRounding: RoundingDirection,
  /** マイナスの金額（値引きの行など）。既定 abs */
  negativeRounding: NegativeRounding,
});
export type RoundingSettings = z.infer<typeof RoundingSettings>;
export const RoundingSettingsUpdate = RoundingSettings;
export type RoundingSettingsUpdate = z.infer<typeof RoundingSettingsUpdate>;

/** PUT /api/v1/partners/:id/tax-rounding。null＝会社の設定に従う。管理者だけ */
/** 顧客の消費税の端数処理（PUT /partners/:id/tax-rounding）。version は Partner.updatedAt（#202。省けば見ない） */
export const PartnerTaxRoundingUpdate = z.object({ taxRounding: RoundingDirection.nullable(), version: Version.optional() });
export type PartnerTaxRoundingUpdate = z.infer<typeof PartnerTaxRoundingUpdate>;
/** PUT /partners/:id/tax-on（#266、docs/96 決めごと13）：税を確定する書類（invoice＝請求書・既定／delivery＝納品書） */
export const PartnerTaxOnUpdate = z.object({ taxOn: z.enum(["invoice", "delivery"]), version: Version.optional() });
export type PartnerTaxOnUpdate = z.infer<typeof PartnerTaxOnUpdate>;
/** PUT /partners/:id/acceptance（#266 1b）：{acceptance}の既定（null＝会社に従う） */
export const PartnerAcceptanceUpdate = z.object({ acceptance: z.boolean().nullable(), version: Version.optional() });
export type PartnerAcceptanceUpdate = z.infer<typeof PartnerAcceptanceUpdate>;

/** 画面の文（2026-10-01 射場さん：簡潔に・業務の言葉で） */
export const ROUNDING_MSG = {
  saved: "端数処理を保存しました（新規の見積から適用）。",
  applied: "既定の端数処理で再計算しました。",
  notDraft: "再計算できるのは未発行の見積のみです。",
  partnerSaved: "消費税の端数処理を保存しました（新規の見積から適用）。",
} as const;
